import axios from "axios";
import { BASE_URL } from "./axios";
import { shouldInterceptCall, getMockResponse } from "./demoApiHandler";
import { isCurrentUserDemo } from "./demoUserConfig";

// Set global axios defaults for larger file uploads
axios.defaults.maxContentLength = 100 * 1024 * 1024; // 100MB - maximum size of the response
axios.defaults.maxBodyLength = 100 * 1024 * 1024; // 100MB - maximum size of the request body

// Use REACT_APP_BACKEND_URL + /api/v1 for more flexibility
// Fallback to localhost if remote server is not accessible
const API = axios.create({
  baseURL: `${process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000'}/api/v1`,
  maxContentLength: 100 * 1024 * 1024, // 100MB - maximum size of the response
  maxBodyLength: 100 * 1024 * 1024, // 100MB - maximum size of the request body
});

// Helper function to check if user is in team member context
const isTeamMemberContext = () => {
  const selectedArtist = localStorage.getItem('selectedArtist');
  return !!selectedArtist;
};

// Helper function to get the effective user ID (artist if in team member context, otherwise current user)
const getEffectiveUserId = () => {
  const selectedArtist = localStorage.getItem('selectedArtist');
  console.log('🔍 getEffectiveUserId - selectedArtist raw:', selectedArtist);
  
  if (selectedArtist) {
    try {
      const artist = JSON.parse(selectedArtist);
      console.log('🔍 getEffectiveUserId - parsed artist object:', artist);
      console.log('🔍 getEffectiveUserId - artist._id:', artist._id);
      console.log('🔍 getEffectiveUserId - artist keys:', Object.keys(artist));
      
      if (artist._id) {
        console.log('🎭 Using artist ID for API call:', artist._id);
        return artist._id;
      } else {
        console.warn('⚠️ Artist object exists but has no _id field!');
        return null;
      }
    } catch (error) {
      console.error('Error parsing selectedArtist:', error);
    }
  }
  
  // Lazy import to avoid circular dependency
  try {
    const { store } = require("../store");
    const state = store.getState();
    const userId = state.user?.userArticle?._id;
    console.log('👤 Using current user ID for API call:', userId);
    return userId;
  } catch (error) {
    console.error('Error accessing store:', error);
    return null;
  }
};

// Helper function to modify request data for delegated access
const modifyRequestForDelegatedAccess = (url, data) => {
  if (!isTeamMemberContext()) {
    return data; // No modification needed
  }

  // Check current route to determine context
  const currentPath = window.location.pathname;
  
  // Team member routes that should NEVER use delegated access
  const teamMemberOnlyRoutes = [
    '/team-member-dashboard',
    '/manager-dashboard', 
    '/team-member/settings',
    '/team-member/billing',
    '/team-member/help',
    '/more/setting/advanced/delegate-access',
    '/more/setting/advanced/team-management'
  ];

  // Check if current route should use team member context
  const isTeamMemberRoute = teamMemberOnlyRoutes.some(route => currentPath.includes(route));
  if (isTeamMemberRoute) {
    console.log(`👤 Team member route detected, skipping delegation: ${currentPath}`);
    return data; // Don't modify for team member routes
  }

  // Skip delegation for team member dashboard specific calls
  const teamMemberEndpoints = [
    'team/managed-artists',
    'team/invited-artists', 
    'team/join-artist',
    'team/members',
    'team/invite',
    'team/request-access',
    'team/leave-team',
    'team/invitation-requests',
    'team/respond-invitation',
    'team/decline-invitation',
    // Analytics endpoints should always use the actual logged-in user (team member)
    // for session consistency, while delegate info is still recorded for reporting
    'analytics-tracking'
  ];

  // Check if this is a team member specific call
  const isTeamMemberCall = teamMemberEndpoints.some(endpoint => url.includes(endpoint));
  if (isTeamMemberCall) {
    console.log(`👤 Team member API call detected, skipping delegation: ${url}`);
    return data; // Don't modify team member calls
  }

  const artistId = getEffectiveUserId();
  const modifiedData = { ...data };

  // List of endpoints that should use the artist's ID instead of team member's
  const delegatedEndpoints = [
    // === CATALOG ===
    'user/getCatalog',
    'user/addCatalog', 
    'user/saveCatalog',
    'user/saveCatalogBatch',
    'user/updateCatalog',
    'user/deleteCatalog',
    'user/discoverCatalogFromArtist',
    'user/saveDiscoveredTracks',
    
    // === USER PROFILE ===
    'user/get-user-article',
    'user/update-user-article',
    'user/getUserArticle',
    'user/updateUserArticle',
    
    // === VAULT ===
    'user/get-vault-data',
    'user/update-vault-data',
    'user/getVaultData',
    'user/updateVaultData',
    'user/update-vault-color-focus',
    'vault/get-data',
    'vault/update',
    'vault/update-vault-color-focus',
    
    // === BOOKINGS ===
    'user/getBookings',
    'user/saveBookings',
    'user/updateBooking',
    'user/deleteBooking',
    'user/getBookingsForAI',
    
    // === MERCH ===
    'user/getMerch',
    'user/saveMerch',
    'user/bulkImportMerch',
    'user/updateMerch',
    'user/deleteMerch',
    
    // === ANALYTICS ===
    'user/analytics',
    'user/getAnalyticsSummary',
    'user/getExpertsAnalytics', 
    'user/getBookingsAnalytics',
    'user/getCatalogAnalytics',
    'user/getMerchAnalytics',
    'user/getPagesAnalytics',
    'user/exportAnalyticsData',
    'analytics/',
    
    // === FOCUS PAGE ===
    'focus/get-data-for-focus',
    'focus/update-focus-data',
    'focus/add-music-for-focus',
    'focus/update-music-for-focus',
    'focus/delete-music-for-focus',
    'focus/add-product-for-focus',
    'focus/update-product-for-focus',
    'focus/delete-product-for-focus',
    'focus/update-color-for-focus',
    'focus/',
    
    // === CONTENT (Music, Video, Events, Products, etc.) ===
    'user/get-music',
    'user/add-music-to-section',
    'user/update-music',
    'user/delete-music',
    'user/update-reordered-music',
    'user/search-music-for-release',
    
    'user/get-video',
    'user/add-video-to-section',
    'user/update-video',
    'user/delete-video',
    'user/update-reordered-video',
    'user/get-video-message',
    'user/add-video-message',
    'user/delete-video-message',
    
    'user/get-event',
    'user/add-event-to-section',
    'user/update-event',
    'user/deletEvent',
    'user/update-reordered-event',
    
    'user/get-product',
    'user/add-product-to-section',
    'user/update-product',
    'user/delete-product',
    'user/delete-bulk-product',
    'user/update-reordered-product',
    
    'user/get-playlist',
    'user/add-playlist-to-section',
    'user/delete-playlist',
    
    'user/get-form',
    'user/add-form-to-section',
    'user/delete-form',
    
    'user/get-socialfeed',
    'user/add-socialfeed-to-section',
    'user/delete-socialfeed',
    'user/update-socialfeed',
    
    'user/get-customlink',
    'user/add-customlink-to-section',
    'user/delete-customlink',
    
    // === AI & PROMPTS ===
    'user/chatWithAI',
    'user/chatWithAIForDizee',
    'user/getCareerAdvice',
    'user/getAIChatHistory',
    'user/getChatHistoryByConversationId',
    'user/prompts',
    'user/preset-prompts',
    
    // === SECTIONS & PAGES ===
    'user/get-all-sections',
    'user/get-pages',
    'user/add-custom-for-brand',
    'user/delete-custom-for-brand',
    
    // === GENERIC PATTERNS ===
    'user/add-',
    'user/update-',
    'user/delete-',
    'user/get-',
    'user/save',
    'pages/',
    'merch/'
  ];

  // Parameters that typically contain user IDs that should be replaced with artist ID
  const userIdParams = ['userId', 'createdBy', 'artistId', 'ownerId'];

  // Check if this endpoint should use delegated access
  const shouldDelegate = delegatedEndpoints.some(endpoint => url.includes(endpoint));
  
  if (shouldDelegate && artistId) {
    console.log(`🎭 Modifying API call for delegated access: ${url} (route: ${currentPath})`);
    console.log(`🎭 Original data:`, data);
    console.log(`🎭 Artist ID to use:`, artistId);
    
    // Replace user ID parameters with artist ID
    userIdParams.forEach(param => {
      if (data && data.hasOwnProperty(param)) {
        console.log(`🎭 Replacing ${param}: ${data[param]} → ${artistId}`);
        modifiedData[param] = artistId;
      }
    });

    // Always add userId for delegated access (not just when data is empty)
    if (!modifiedData.userId) {
      modifiedData.userId = artistId;
      console.log(`🎭 Adding userId to request: ${artistId}`);
    }
    
    console.log(`🎭 Final modified data:`, modifiedData);
  } else {
    console.log(`👤 Not delegating API call: ${url}`);
    console.log(`👤 shouldDelegate: ${shouldDelegate}, artistId: ${artistId}`);
  }

  return modifiedData;
};

export const postDataAPI = async (url, post, token, options = {}) => {
    // Demo user handling - allow connectors/OAuth and AI chat, block other write operations
    const allowedDemoEndpoints = [
        'connectors',
        'oauth',
        'google',
        'calendar',
        'spotify',
        'integrations',
        'callback',
        'chatWithAI',
        'getCareerAdvice',
        'appendMessageToConversation'
    ];
    
    const isDemoAllowed = allowedDemoEndpoints.some(endpoint => url.includes(endpoint));
    
    if (isCurrentUserDemo() && !isDemoAllowed) {
        console.log('🎭 Demo mode: Blocking POST call to', url);
        return Promise.resolve({
            data: {
                status: 200,
                message: 'Demo mode - changes not saved',
                data: post
            }
        });
    }
    
    // Apply delegated access modification
    const modifiedPost = modifyRequestForDelegatedAccess(url, post);
    
    const isFormData = modifiedPost instanceof FormData;
    console.log(` postDataAPI token::`, token);
    console.log('BASE_URL being used:', BASE_URL);
    console.log('Full URL will be:', `${BASE_URL}/${url}`);
    const config = {
        headers: {
            'Content-Type': isFormData ? 'multipart/form-data' : 'application/json',
            'Authorization': token
        },
        maxContentLength: 100 * 1024 * 1024, // 100MB - maximum size of the response
        maxBodyLength: 100 * 1024 * 1024, // 100MB - maximum size of the request body
        ...options // Spread additional options like timeout
    }

    try {
        // Calculate and log request size for debugging
        let requestSize = 0;
        if (modifiedPost) {
            if (modifiedPost instanceof FormData) {
                // FormData size is harder to calculate, estimate
                requestSize = -1; // Unknown for FormData
            } else {
                const jsonString = JSON.stringify(modifiedPost);
                requestSize = new Blob([jsonString]).size;
            }
        }
        
        const sizeMB = requestSize > 0 ? (requestSize / (1024 * 1024)).toFixed(2) : 'unknown';
        console.log('Making request to:', `${BASE_URL}/${url}`);
        console.log('📦 Request payload size:', `${sizeMB}MB`, requestSize > 0 ? `(${requestSize} bytes)` : '(FormData - size unknown)');
        console.log('Request config:', {
            headers: config.headers,
            data: modifiedPost instanceof FormData ? '[FormData]' : (requestSize > 5 * 1024 * 1024 ? '[Large payload - truncated in log]' : modifiedPost),
            timeout: config.timeout
        });

        const res = await axios.post(`${BASE_URL}/${url}`, modifiedPost, config);
        return res;
    } catch (err) {
        console.error('Request failed:', {
            url: `${BASE_URL}/${url}`,
            status: err.response?.status,
            statusText: err.response?.statusText,
            data: err.response?.data,
            timeout: err.code === 'ECONNABORTED' ? 'Request timed out' : undefined
        });
        throw err;
    }
};

export const getDataAPI = async (url, token) => {
    // Demo user handling - DON'T intercept GET calls, let them go through or fail
    // This allows apps to load properly
    
    // Apply delegated access modification for GET requests that might need user context
    const modifiedUrl = modifyUrlForDelegatedAccess(url);
    
    console.log('BASE_URL being used:', BASE_URL);
    console.log('Full URL will be:', `${BASE_URL}/${modifiedUrl}`);
    
    try {
        const res = await axios.get(`${BASE_URL}/${modifiedUrl}`, {
            headers: {
                'Authorization': token
            }
        });
    return res;
    } catch (err) {
        console.error('GET request failed:', {
            url: `${BASE_URL}/${modifiedUrl}`,
            status: err.response?.status,
            statusText: err.response?.statusText,
            data: err.response?.data
        });
        
        // For demo users, return empty data instead of throwing
        if (isCurrentUserDemo()) {
            console.log('🎭 Demo mode: GET failed, returning empty data');
            return {
                data: {
                    status: 200,
                    message: 'Demo mode - no data',
                    data: []
                }
            };
        }
        
        throw err;
    }
};

// Helper function to modify URL for delegated access (for GET requests)
const modifyUrlForDelegatedAccess = (url) => {
  if (!isTeamMemberContext()) {
    return url; // No modification needed
  }

  // Check current route to determine context
  const currentPath = window.location.pathname;
  
  // Team member routes that should NEVER use delegated access
  const teamMemberOnlyRoutes = [
    '/team-member-dashboard',
    '/manager-dashboard', 
    '/team-member/settings',
    '/team-member/billing',
    '/team-member/help',
    '/more/setting/advanced/delegate-access',
    '/more/setting/advanced/team-management'
  ];

  // Check if current route should use team member context
  const isTeamMemberRoute = teamMemberOnlyRoutes.some(route => currentPath.includes(route));
  if (isTeamMemberRoute) {
    console.log(`👤 Team member route detected, skipping URL delegation: ${currentPath}`);
    return url; // Don't modify for team member routes
  }

  const artistId = getEffectiveUserId();
  
  // List of GET endpoints that should use the artist's ID
  const delegatedGetEndpoints = [
    // === CATALOG ===
    'user/getCatalog',
    
    // === USER PROFILE ===
    'user/get-user-article',
    'user/getUserArticle',
    
    // === VAULT ===
    'user/get-vault-data',
    'user/getVaultData',
    'vault/get-data',
    
    // === BOOKINGS ===
    'user/getBookings',
    'user/getBookingsForAI',
    
    // === MERCH ===
    'user/getMerch',
    
    // === ANALYTICS ===
    'user/analytics',
    'user/getAnalyticsSummary',
    'user/getExpertsAnalytics',
    'user/getBookingsAnalytics', 
    'user/getCatalogAnalytics',
    'user/getMerchAnalytics',
    'user/getPagesAnalytics',
    'user/exportAnalyticsData',
    'analytics/get-',
    'analytics/summary',
    'analytics/experts',
    'analytics/bookings',
    'analytics/catalog',
    'analytics/merch',
    'analytics/pages',
    'analytics/export',
    
    // === FOCUS PAGE ===
    'focus/get-data-for-focus',
    
    // === CONTENT ===
    'user/get-music',
    'user/get-video',
    'user/get-video-message',
    'user/get-event',
    'user/get-product',
    'user/get-playlist',
    'user/get-form',
    'user/get-socialfeed',
    'user/get-customlink',
    'user/get-customlinksection',
    
    // === AI & PROMPTS ===
    'user/getAIChatHistory',
    'user/getChatHistoryByConversationId',
    'user/prompts',
    'user/preset-prompts',
    
    // === SECTIONS & PAGES ===
    'user/get-all-sections',
    'user/get-pages',
    'user/get-generated-private-link',
    
    // === GENERIC PATTERNS ===
    'user/get-',
    'focus/get-',
    'vault/get-',
    'analytics/get-'
  ];

  // Check if this endpoint should use delegated access
  const shouldDelegate = delegatedGetEndpoints.some(endpoint => url.includes(endpoint));
  
  if (shouldDelegate && artistId) {
    console.log(`🎭 Modifying GET URL for delegated access: ${url} (route: ${currentPath})`);
    
    // For GET requests, we typically add the userId as a query parameter
    const separator = url.includes('?') ? '&' : '?';
    const modifiedUrl = `${url}${separator}userId=${artistId}`;
    console.log(`🎭 Modified URL: ${modifiedUrl}`);
    return modifiedUrl;
  }

  return url;
};

export const putDataAPI = async (url, post, token) => {
    // Demo user handling - allow connectors/OAuth and AI chat, block other write operations
    const allowedDemoEndpoints = [
        'connectors',
        'oauth',
        'google',
        'calendar',
        'spotify',
        'integrations',
        'callback',
        'conversations/'
    ];
    
    const isDemoAllowed = allowedDemoEndpoints.some(endpoint => url.includes(endpoint));
    
    if (isCurrentUserDemo() && !isDemoAllowed) {
        console.log('🎭 Demo mode: Blocking PUT call to', url);
        return Promise.resolve({
            data: {
                status: 200,
                message: 'Demo mode - changes not saved',
                data: post
            }
        });
    }
    
    // Apply delegated access modification
    const modifiedPost = modifyRequestForDelegatedAccess(url, post);
    
    console.log('PUT API - Original data:', post);
    console.log('PUT API - Modified data:', modifiedPost);
    console.log('PUT API - URL:', url);

    const res = await axios.put(`${BASE_URL}/${url}`, modifiedPost,
        {
            headers: { Authorization: token }
        })

    return res;
}

export const deleteDataAPI = async (url, token) => {
    // Demo user handling - allow connectors/OAuth and AI chat, block other delete operations
    const allowedDemoEndpoints = [
        'connectors',
        'oauth',
        'google',
        'calendar',
        'spotify',
        'integrations',
        'callback',
        'conversations/'
    ];
    
    const isDemoAllowed = allowedDemoEndpoints.some(endpoint => url.includes(endpoint));
    
    if (isCurrentUserDemo() && !isDemoAllowed) {
        console.log('🎭 Demo mode: Blocking DELETE call to', url);
        return Promise.resolve({
            data: {
                status: 200,
                message: 'Demo mode - deletion not allowed',
                data: null
            }
        });
    }
    
    // Apply delegated access modification for DELETE requests that might need user context
    const modifiedUrl = modifyUrlForDelegatedAccess(url);
    
    console.log('DELETE API - Original URL:', url);
    console.log('DELETE API - Modified URL:', modifiedUrl);

    const res = await axios.delete(`${BASE_URL}/${modifiedUrl}`,
        {
            headers: { Authorization: token }
        })

    return res;
}